import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material"
import { useEffect, useState, type JSX } from "react"

import { CustomerSearchPicker, type CustomerPick } from "@crm-src/components/purchase-orders/customer-search-picker"
import { preShipApi, type PreShipQuestionnaire } from "@crm-src/lib/pre-ship-api"

export interface NewPreShipDialogProps {
  open: boolean
  onClose: () => void
  onCreated: (q: PreShipQuestionnaire) => void
}

/**
 * CRM ▸ Pre-ship Questionnaires ▸ New questionnaire: the ONE question is
 * which customer it is for. There is nothing else to choose (CEO,
 * 2026-10-03) — crm-api creates it with the Basic information section filled
 * in, and the editor takes over. (On a customer profile the Pre-ship tab
 * already knows the customer, so it creates without this dialog.)
 */
export function NewPreShipDialog({ open, onClose, onCreated }: NewPreShipDialogProps): JSX.Element {
  const [customer, setCustomer] = useState<CustomerPick | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setCustomer(null)
    setError(null)
  }, [open])

  const submit = async () => {
    if (!customer) return
    setSaving(true)
    setError(null)
    try {
      onCreated(await preShipApi.create({ customer_id: customer.id }))
    } catch (err: any) {
      setError(err?.response?.data?.error || "The questionnaire could not be created.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="xs" fullWidth data-cy="preship-new-dialog">
      <DialogTitle>New pre-ship questionnaire</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
        <CustomerSearchPicker value={customer} onChange={setCustomer} />
        {error && <Typography sx={{ color: "error.main", fontSize: 13 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={saving || !customer} data-cy="preship-new-create">
          {saving ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
